import type { HeavenlyStem } from '../types/stems';
import type { EarthlyBranch } from '../types/branches';
import type { Pillar } from '../types/pillars';
import { HEAVENLY_STEMS, EARTHLY_BRANCHES, SEXAGENARY_CYCLE } from './constants';

/** Create a Pillar from a sexagenary index (0-59) */
export function makePillar(sexagenaryIndex: number): Pillar {
  const idx = ((sexagenaryIndex % 60) + 60) % 60; // normalize to 0-59
  const stem = HEAVENLY_STEMS[idx % 10];
  const branch = EARTHLY_BRANCHES[idx % 12];
  return {
    stem,
    branch,
    ganji: SEXAGENARY_CYCLE[idx],
    sexagenaryIndex: idx,
  };
}

/** Create a Pillar from stem and branch */
export function makePillarFromStemBranch(stem: HeavenlyStem, branch: EarthlyBranch): Pillar {
  const stemIdx = HEAVENLY_STEMS.indexOf(stem);
  const branchIdx = EARTHLY_BRANCHES.indexOf(branch);

  // Validate: stem and branch must have the same parity (both yang or both yin)
  if (stemIdx % 2 !== branchIdx % 2) {
    throw new Error(`Invalid stem-branch combination: ${stem}${branch} (mismatched yin-yang parity)`);
  }

  // Find sexagenary index where idx%10 === stemIdx and idx%12 === branchIdx
  let idx = stemIdx;
  while (idx % 12 !== branchIdx) {
    idx += 10;
  }
  idx = idx % 60;
  return {
    stem,
    branch,
    ganji: `${stem}${branch}`,
    sexagenaryIndex: idx,
  };
}

/** Get the next pillar in forward direction */
export function nextPillar(pillar: Pillar, steps: number = 1): Pillar {
  return makePillar(pillar.sexagenaryIndex + steps);
}

/** Get the previous pillar in reverse direction */
export function prevPillar(pillar: Pillar, steps: number = 1): Pillar {
  return makePillar(pillar.sexagenaryIndex - steps);
}

/** Get stem index (0-9) */
export function stemIndex(stem: HeavenlyStem): number {
  return HEAVENLY_STEMS.indexOf(stem);
}

/** Get branch index (0-11) */
export function branchIndex(branch: EarthlyBranch): number {
  return EARTHLY_BRANCHES.indexOf(branch);
}
